import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Button,
  useTheme,
  Stack,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Checkbox,
  Modal,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BaseLabel, LabelType, LabelCategory, labelsService } from '../../services/labels-service';
import { Language, languageService } from '../../services/language-service';
import { translationService } from '../../services/translation-service';
import { config } from '../../config/config';
import LanguagesImg from '../../assets/images/Languages.png';
import AIIcon from '../../assets/images/Artificial_intelligence.png';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface EditLabelPanelProps {
  open: boolean;
  onClose: () => void;
  label: BaseLabel | null;
  onSave?: () => void;
}

interface Translation {
  LabelText: string;
  Description: string;
}

interface InitialValues {
  LabelKey: string;
  LabelText: string;
  LabelType: LabelType;
  Category: LabelCategory;
  Description: string;
  IsActive: boolean;
  Translations: Record<string, Translation>;
}

const LABEL_TYPES: LabelType[] = ['BUTTON', 'TITLE', 'MESSAGE', 'ERROR', 'ANY', 'FOOTNOTE', 'TOOLTIP'];
const LABEL_CATEGORIES: LabelCategory[] = ['COLLECTION_POINT', 'EMAIL', 'GENERAL'];

const EditLabelPanel: React.FC<EditLabelPanelProps> = ({ open, onClose, label, onSave }) => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [editedLabel, setEditedLabel] = useState<Partial<BaseLabel>>({});
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(['en']);
  const [activeLanguage, setActiveLanguage] = useState<string>('en');
  const [translations, setTranslations] = useState<Record<string, Translation>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState<InitialValues | undefined>();
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStartTranslation, setShowStartTranslation] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [acceptedTranslations, setAcceptedTranslations] = useState<Set<string>>(new Set());
  const [originalTranslations, setOriginalTranslations] = useState<Record<string, string>>({});

  const DRAWER_WIDTH = theme.customLayout.drawerWidth;
  const HEADER_HEIGHT = theme.customLayout.headerHeight;
  const MEDIUM_GAP = theme.customLayout.mediumGap;

  const checkForChanges = (): boolean => {
    if (!initialValues) return false;
    
    if (editedLabel.LabelKey !== initialValues.LabelKey ||
        editedLabel.LabelText !== initialValues.LabelText ||
        editedLabel.LabelType !== initialValues.LabelType ||
        editedLabel.Category !== initialValues.Category ||
        editedLabel.Description !== initialValues.Description ||
        editedLabel.IsActive !== initialValues.IsActive) {
      return true;
    }

    const currentTranslation = translations[activeLanguage];
    if (currentTranslation) {
      const originalTranslation = initialValues.Translations[activeLanguage];
      if (originalTranslation) {
        if (currentTranslation.LabelText !== originalTranslation.LabelText ||
            currentTranslation.Description !== originalTranslation.Description) {
          return true;
        }
      }
    }

    return false;
  };

  useEffect(() => {
    if (!open) {
      // Reset states when panel closes
      setCurrentTab(0);
      setActiveLanguage('en');
      setSelectedLanguages(['en']);
      return;
    }
    
    setCurrentTab(0);
    setIsLoading(true);
    if (label) {
      setEditedLabel(label);
      // Initialize translations
      const newTranslations = {
        en: {
          LabelText: label.LabelText,
          Description: label.Description || '',
        }
      };
      setTranslations(newTranslations);
      setInitialValues({
        LabelKey: label.LabelKey,
        LabelText: label.LabelText,
        LabelType: label.LabelType,
        Category: label.Category,
        Description: label.Description || '',
        IsActive: label.IsActive,
        Translations: newTranslations
      });

      // Fetch existing translations
      labelsService.getTranslations(label.LabelId)
        .then(existingTranslations => {
          if (existingTranslations.length === 0) {
            setShowStartTranslation(true);
            return;
          }
          setShowStartTranslation(false);
          const translationsMap: Record<string, Translation> = {
            en: {
              LabelText: label.LabelText,
              Description: label.Description || '',
            }
          };
          
          const accepted = new Set<string>();
          const original: Record<string, string> = {};
          existingTranslations.forEach(translation => {
            if (translation.LanguageCode !== 'en') {
              translationsMap[translation.LanguageCode] = {
                LabelText: translation.Translation,
                Description: '',
              };
              accepted.add(translation.LanguageCode);
              original[translation.LanguageCode] = translation.Translation;
            }
          });
          
          setTranslations(translationsMap);
          setAcceptedTranslations(accepted);
          setOriginalTranslations(original);
          setInitialValues(prev => prev ? {
            ...prev,
            Translations: translationsMap
          } : undefined);
        })
        .catch(error => {
          console.error('Failed to fetch translations:', error);
          setError('Failed to load translations');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    // Fetch supported languages from the API
    Promise.all([
      languageService.getSupportedLanguages()
        .then(languages => {
          setLanguages(languages);
          // Set the first non-English language as active if available
          const firstNonEnglishLang = languages.find(lang => lang.code !== 'en');
          if (firstNonEnglishLang) {
            setActiveLanguage(firstNonEnglishLang.code);
            setSelectedLanguages([firstNonEnglishLang.code]);
          }
        })
        .catch(error => {
          console.error('Failed to fetch supported languages:', error);
          setError('Failed to load supported languages');
        })
    ]).finally(() => {
      setIsLoading(false);
    });
    setHasChanges(false);
  }, [label, open]);

  const handleLanguageToggle = (code: string) => {
    setSelectedLanguages((prev: string[]) =>
      prev.includes(code) ? prev.filter((c: string) => c !== code) : [...prev, code]
    );
    if (!translations[code]) {
      setTranslations((prev: Record<string, Translation>) => ({ 
        ...prev, 
        [code]: { 
          LabelText: '', 
          Description: '',
        } 
      }));
    }
  };

  const handleTextChange = (field: keyof BaseLabel) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditedLabel(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSelectChange = (field: keyof BaseLabel) => (
    event: SelectChangeEvent
  ) => {
    setEditedLabel(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleTranslationChange = (field: 'labelText' | 'description', value: string) => {
    setTranslations(prev => ({
      ...prev,
      [activeLanguage]: {
        ...prev[activeLanguage],
        [field === 'labelText' ? 'LabelText' : 'Description']: value,
      },
    }));
  };

  // Add useEffect to update hasChanges when any relevant field changes
  useEffect(() => {
    setHasChanges(checkForChanges());
  }, [editedLabel, translations, activeLanguage, initialValues]);

  const handleSubmit = async () => {
    if (!label) return;
    setIsSavingDetails(true);
    setError(null);
    
    try {
      await labelsService.updateBaseLabel(label.LabelId, editedLabel);
      onSave?.();
      onClose();
    } catch (error: any) {
      // Log the complete error response from server
      console.error('Server error response:', error.response?.data || error);
      // Display the specific error message from the server response
      setError(error.response?.data?.message || 'Failed to update label');
    } finally {
      setIsSavingDetails(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleTranslateAI = async () => {
    if (!translations['en']?.LabelText) {
      setError('Please ensure English text is available for translation');
      return;
    }

    setIsTranslating(true);
    setError(null);

    try {
      const translatedText = await translationService.translate(
        translations['en'].LabelText,
        activeLanguage
      );

      setTranslations(prev => ({
        ...prev,
        [activeLanguage]: {
          ...prev[activeLanguage],
          LabelText: translatedText
        }
      }));
    } catch (error: any) {
      console.error('Translation error:', error);
      setError(error.response?.data?.message || 'Failed to translate text');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleAcceptTranslation = async () => {
    if (!label?.LabelId || !translations[activeLanguage]?.LabelText) {
      setError('Please ensure translation text is available');
      return;
    }

    setIsSavingDetails(true);
    setError(null);

    try {
      await labelsService.updateTranslation(
        label.LabelId,
        activeLanguage,
        translations[activeLanguage].LabelText
      );

      // Add to accepted translations and update original
      setAcceptedTranslations(prev => new Set(Array.from(prev).concat(activeLanguage)));
      setOriginalTranslations(prev => ({
        ...prev,
        [activeLanguage]: translations[activeLanguage].LabelText
      }));

      // Refresh translations list
      const updatedTranslations = await labelsService.getTranslations(label.LabelId);
      const translationsMap: Record<string, Translation> = {
        en: {
          LabelText: label.LabelText,
          Description: label.Description || '',
        }
      };
      
      const accepted = new Set<string>();
      const original: Record<string, string> = {};
      updatedTranslations.forEach(translation => {
        if (translation.LanguageCode !== 'en') {
          translationsMap[translation.LanguageCode] = {
            LabelText: translation.Translation,
            Description: '',
          };
          accepted.add(translation.LanguageCode);
          original[translation.LanguageCode] = translation.Translation;
        }
      });
      
      setTranslations(translationsMap);
      setAcceptedTranslations(accepted);
      setOriginalTranslations(original);
    } catch (error: any) {
      console.error('Error saving translation:', error);
      setError(error.response?.data?.message || 'Failed to save translation');
    } finally {
      setIsSavingDetails(false);
    }
  };

  // Update the Accept button to check for changes
  const hasTranslationChanges = () => {
    const currentText = translations[activeLanguage]?.LabelText || '';
    const originalText = originalTranslations[activeLanguage] || '';
    return currentText !== originalText;
  };

  // Check for changes across all translations
  const hasAnyTranslationChanges = () => {
    return Object.keys(translations).some(langCode => {
      if (langCode === 'en') return false; // Skip English as it's the source
      const currentText = translations[langCode]?.LabelText || '';
      const originalText = originalTranslations[langCode] || '';
      return currentText !== originalText;
    });
  };

  const handleActivateLabel = async () => {
    if (editedLabel.IsPublished) {
      // If already published, show confirmation for unpublishing
      const confirmUnpublish = window.confirm(
        'Are you sure you want to unpublish this label?\n\n' +
        'Unpublishing will make this label unavailable to other parts of the program.\n' +
        'You can publish it again later when needed.'
      );

      if (!confirmUnpublish) {
        return;
      }

      try {
        setIsPublishing(true);
        await labelsService.updatePublishStatus(label!.LabelId, false);
        setEditedLabel(prev => ({ ...prev, IsPublished: false }));
        onSave?.();
        onClose(); // Close the modal after successful unpublish
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to unpublish label');
      } finally {
        setIsPublishing(false);
      }
      return;
    }

    // Step 1: Check for unsaved changes in both tabs
    const hasDetailsChanges = hasChanges;
    const hasTranslationChanges = hasAnyTranslationChanges();

    if (hasDetailsChanges || hasTranslationChanges) {
      setError('Please save all changes before publishing the label');
      return;
    }

    // Step 2: Validate Details tab
    // Validate label text
    if (!editedLabel.LabelText || editedLabel.LabelText.length < 3) {
      setError('Label text must be at least 3 characters long');
      return;
    }

    // Validate description if present
    if (editedLabel.Description && editedLabel.Description.length < 3) {
      setError('Description must be at least 3 characters long if provided');
      return;
    }

    // Validate type and category
    if (!editedLabel.LabelType || !editedLabel.Category) {
      setError('Label type and category must be selected');
      return;
    }

    // Step 3: Validate Translations tab
    const supportedLanguages = languages.filter(lang => lang.translationProvider !== null);
    const missingTranslations = supportedLanguages.filter(lang => 
      !translations[lang.code]?.LabelText || 
      translations[lang.code].LabelText.length < 3
    );

    // Store missing languages list for use in confirmation dialog
    const missingLanguagesList = missingTranslations.length > 0 ? 
      missingTranslations.map(l => l.name).join(', ') : '';

    // Check for unaccepted translations
    const unacceptedTranslations = supportedLanguages.filter(lang => 
      translations[lang.code]?.LabelText && 
      !acceptedTranslations.has(lang.code)
    );

    if (unacceptedTranslations.length > 0) {
      setError(`Please accept translations for: ${unacceptedTranslations.map(l => l.name).join(', ')}`);
      return;
    }

    // If all validations pass, show confirmation dialog
    const confirmMessage = `Are you sure you want to publish this label?\n\n` +
      `Publishing will make this label available to other parts of the program to use.\n\n` +
      `Please ensure that you have:\n` +
      `• Reviewed all translations\n` +
      `• Verified the label text and description\n` +
      `• Selected the correct label type and category\n\n` +
      `${missingTranslations.length > 0 ? 
          `⚠️  WARNING: Missing translations for: ${missingLanguagesList}\n` +
          `The label will be published without these translations.\n\n` : 
          ''
      }` +
      `This action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // If user confirms, publish the label
    try {
      setIsPublishing(true);
      await labelsService.updatePublishStatus(label!.LabelId, true);
      setEditedLabel(prev => ({ ...prev, IsPublished: true }));
      onSave?.();
      onClose(); // Close the modal after successful publish
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to publish label');
    } finally {
      setIsPublishing(false);
    }
  };

  if (!label) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit-label-modal"
      sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', zIndex: 1301 }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[24],
          p: 0,
          width: `calc(100vw - ${DRAWER_WIDTH + 2 * MEDIUM_GAP}px)`,
          height: `calc(100vh - ${HEADER_HEIGHT + 2 * MEDIUM_GAP}px)`,
          position: 'absolute',
          left: `${DRAWER_WIDTH + MEDIUM_GAP}px`,
          top: `${HEADER_HEIGHT + MEDIUM_GAP}px`,
          right: `${MEDIUM_GAP}px`,
          bottom: `${MEDIUM_GAP}px`,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <Box sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            Edit Label
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleActivateLabel}
              disabled={isPublishing || isSavingDetails || isDeleting || isLoading}
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  bgcolor: theme.palette.primary.dark,
                },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                }
              }}
            >
              {isPublishing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                editedLabel.IsPublished ? 'Unpublish Label' : 'Publish Label'
              )}
            </Button>
            <IconButton
              onClick={handleClose}
              disabled={isPublishing || isSavingDetails || isDeleting}
              sx={{
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Tabs
            value={currentTab}
            onChange={(_, v) => setCurrentTab(v)}
            sx={{
              '& .MuiTab-root': {
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Details
                  {hasChanges && currentTab === 0 && (
                    <Typography 
                      component="span" 
                      sx={{ 
                        color: theme.palette.primary.main,
                        opacity: 0.8,
                        fontSize: '1.2rem',
                        lineHeight: 1,
                        ml: 0.5,
                        fontWeight: 500
                      }}
                    >
                      •
                    </Typography>
                  )}
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Translations
                  {currentTab === 1 && hasAnyTranslationChanges() && (
                    <Typography 
                      component="span" 
                      sx={{ 
                        color: theme.palette.primary.main,
                        opacity: 0.8,
                        fontSize: '1.2rem',
                        lineHeight: 1,
                        ml: 0.5,
                        fontWeight: 500
                      }}
                    >
                      •
                    </Typography>
                  )}
                </Box>
              } 
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: theme.palette.background.default }}>
          {currentTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Label Key
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.shape.borderRadius,
                      color: theme.palette.text.primary,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    }}
                  >
                    {editedLabel.LabelKey}
                  </Box>
                </Box>

                <FormControl fullWidth size="small">
                  <InputLabel id="label-type-label">Label Type</InputLabel>
                  <Select
                    labelId="label-type-label"
                    id="label-type-select"
                    value={editedLabel.LabelType || 'ANY'}
                    onChange={handleSelectChange('LabelType')}
                    label="Label Type"
                    native
                    disabled={editedLabel.IsPublished}
                    sx={{
                      '& .MuiNativeSelect-select': {
                        padding: '8.5px 14px'
                      }
                    }}
                  >
                    {LABEL_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel id="category-label">Category</InputLabel>
                  <Select
                    labelId="category-label"
                    id="category-select"
                    value={editedLabel.Category || 'GENERAL'}
                    onChange={handleSelectChange('Category')}
                    label="Category"
                    native
                    disabled={editedLabel.IsPublished}
                    sx={{
                      '& .MuiNativeSelect-select': {
                        padding: '8.5px 14px'
                      }
                    }}
                  >
                    {LABEL_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Label Text"
                  value={editedLabel.LabelText}
                  onChange={handleTextChange('LabelText')}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  size="small"
                  disabled={editedLabel.IsPublished}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                  }}
                />

                <TextField
                  label="Description"
                  value={editedLabel.Description}
                  onChange={handleTextChange('Description')}
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  disabled={editedLabel.IsPublished}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                  }}
                />

                {!editedLabel.IsPublished && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!hasChanges || isSavingDetails || isPublishing || isDeleting}
                      onClick={handleSubmit}
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        },
                        '&.Mui-disabled': {
                          bgcolor: theme.palette.action.disabledBackground,
                          color: theme.palette.action.disabled,
                        },
                        minWidth: 100,
                      }}
                    >
                      {isSavingDetails ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </Box>
                )}

                {editedLabel.IsActive && (
                  <Box sx={{ 
                    mt: 4, 
                    p: 3, 
                    border: `1px solid ${theme.palette.error.main}`,
                    borderRadius: theme.shape.borderRadius,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.08)' : 'rgba(211, 47, 47, 0.04)',
                  }}>
                    <Typography variant="h6" sx={{ color: theme.palette.error.main, mb: 1 }}>
                      Danger Zone
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.error.main, mb: 2 }}>
                      Label once deleted cannot be retrieved
                    </Typography>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this label? This action cannot be undone.')) {
                          if (label?.LabelId) {
                            try {
                              setIsDeleting(true);
                              await labelsService.deleteBaseLabel(label.LabelId);
                              onSave?.();
                              onClose();
                            } catch (error: any) {
                              // Check if it's a permission error
                              if (error.response?.status === 403) {
                                setError('You do not have permission to delete this label');
                              } else if (error.response?.status === 404) {
                                setError('Label not found');
                              } else {
                                setError(error.response?.data?.message || 'Failed to delete label');
                              }
                            } finally {
                              setIsDeleting(false);
                            }
                          }
                        }
                      }}
                      disabled={isDeleting || isPublishing || isSavingDetails}
                      sx={{
                        bgcolor: theme.palette.error.main,
                        '&:hover': {
                          bgcolor: theme.palette.error.dark,
                        },
                      }}
                    >
                      {isDeleting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Delete Label'
                      )}
                    </Button>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {currentTab === 1 && showStartTranslation ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
            }}>
              <img src={LanguagesImg} alt="Languages" style={{ width: 80, height: 80, marginBottom: 24 }} />
              <Typography variant="body1" sx={{ mb: 2 }}>
                Use this space to translate the Purpose in 22 languages as required by the DPDP Act.<br />
                Please ensure that you have finalized the text in the default language before proceeding.
              </Typography>
              <Button
                variant="contained"
                color="success"
                sx={{ mb: 2, px: 4 }}
                onClick={() => {
                  setShowStartTranslation(false);
                  if (languages.length > 0) {
                    setActiveLanguage(languages[0].code);
                    setSelectedLanguages([languages[0].code]);
                  }
                }}
              >
                START TRANSLATION
              </Button>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                PS. We provide translation using AI :)
              </Typography>
            </Box>
          ) : null}

          {currentTab === 1 && !showStartTranslation && (
            <Box sx={{ p: 3, display: 'flex', gap: 3, height: `calc(100% - ${MEDIUM_GAP}px)` }}>
              {/* Table-like language list */}
              <Box sx={{ 
                minWidth: 380, 
                height: '100%', 
                maxHeight: '100%', 
                overflowY: 'auto', 
                border: `1px solid ${theme.palette.divider}`, 
                borderRadius: theme.shape.borderRadius, 
                p: 0, 
                bgcolor: theme.palette.background.paper, 
                mb: `${MEDIUM_GAP}px`, 
                display: 'flex', 
                flexDirection: 'column' 
              }}>
                {/* Header Row */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  px: 2, 
                  py: 1, 
                  borderBottom: `1px solid ${theme.palette.divider}`, 
                  bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50], 
                  fontWeight: 600 
                }}>
                  <Typography variant="body2" sx={{ flex: 2, fontWeight: 600, color: theme.palette.text.primary }}>Languages</Typography>
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, color: theme.palette.text.primary }}>Code</Typography>
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, textAlign: 'center', color: theme.palette.text.primary }}>Accepted</Typography>
                </Box>
                {/* Language Rows */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {languages.map((lang) => {
                    const isSelected = activeLanguage === lang.code;
                    const isNotSupported = lang.translationProvider === null;
                    const rowContent = (
                      <Box
                        key={lang.code}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 2,
                          py: 1,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          bgcolor: isSelected ? theme.palette.action.selected : 'inherit',
                          opacity: isNotSupported ? 0.5 : 1,
                          transition: 'background 0.2s',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: isNotSupported ? undefined : theme.palette.action.hover,
                          },
                        }}
                        onClick={() => setActiveLanguage(lang.code)}
                      >
                        <Box sx={{ flex: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, display: 'inline', color: theme.palette.text.primary }}>{lang.name}</Typography>
                          <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary, display: 'block', ml: 0.5 }}>
                            {lang.localeName}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>{lang.code.toUpperCase()}</Typography>
                          {lang.code !== 'en' && translations[lang.code]?.LabelText !== originalTranslations[lang.code] && (
                            <Typography 
                              component="span" 
                              sx={{ 
                                color: theme.palette.primary.main,
                                fontSize: '0.875rem',
                                lineHeight: 1,
                                fontWeight: 600
                              }}
                            >
                              •
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                          <Checkbox
                            checked={acceptedTranslations.has(lang.code)}
                            readOnly
                            size="small"
                            sx={{ p: 0 }}
                          />
                        </Box>
                      </Box>
                    );
                    return isNotSupported ? (
                      <Tooltip key={lang.code} title="This language is not supported by AI" arrow>
                        <span style={{ display: 'block' }}>{rowContent}</span>
                      </Tooltip>
                    ) : rowContent;
                  })}
                </Box>
              </Box>

              {/* Translation form for selected language */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ mb: 2, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                    English Reference
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    {translations['en']?.LabelText || ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                    Language: {languages.find((l) => l.code === activeLanguage)?.name || activeLanguage}
                  </Typography>
                  {!editedLabel.IsPublished && (
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={isTranslating ? <CircularProgress size={20} /> : <img src={AIIcon} alt="AI" style={{ width: 20, height: 20 }} />}
                      sx={{
                        ml: 2,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        '&:hover': {
                          borderColor: theme.palette.primary.dark,
                          color: theme.palette.primary.dark,
                        },
                      }}
                      disabled={languages.find((l) => l.code === activeLanguage)?.translationProvider === null || isTranslating}
                      onClick={handleTranslateAI}
                    >
                      {isTranslating ? 'TRANSLATING...' : 'TRANSLATE USING AI'}
                    </Button>
                  )}
                  <Tooltip
                    title="AI-generated translations may contain errors or inaccuracies. Please review and cross-check the translation before accepting."
                    arrow
                  >
                    <InfoOutlinedIcon 
                      fontSize="small" 
                      sx={{ 
                        ml: 1, 
                        color: theme.palette.neutral ? theme.palette.neutral.main : theme.palette.text.secondary, 
                        cursor: 'pointer',
                        fontWeight: 400
                      }} 
                    />
                  </Tooltip>
                </Box>
                <TextField
                  label="Label Text"
                  fullWidth
                  size="small"
                  required
                  value={translations[activeLanguage]?.LabelText || ''}
                  onChange={e => handleTranslationChange('labelText', e.target.value)}
                  disabled={editedLabel.IsPublished}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                  }}
                />
                {!editedLabel.IsPublished && (
                  <Button 
                    variant="contained" 
                    sx={{ 
                      alignSelf: 'flex-start', 
                      mt: 2,
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    }}
                    onClick={handleAcceptTranslation}
                    disabled={isSavingDetails || !translations[activeLanguage]?.LabelText || !hasTranslationChanges()}
                  >
                    {isSavingDetails ? <CircularProgress size={24} color="inherit" /> : 'Accept'}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Add Snackbar for error messages */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={config.ui.snackbar.autoHideDuration}
          onClose={() => setError(null)}
          anchorOrigin={config.ui.snackbar.anchorOrigin}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  );
};

export default EditLabelPanel; 